import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import "./clientEvent.css";
import { useAuth } from "../../context/AuthContext";
import useTokenFromUrl from "../../hooks/useTokenFromUrl";
import { fetchAllEvents } from "./clientEventService";

const EventListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading: tokenLoading } = useTokenFromUrl();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoginAlert, setShowLoginAlert] = useState(false);

  const handleMyBookingsClick = () => {
    if (!user) { setShowLoginAlert(true); return; }
    navigate("/my-bookings");
  };

  const formatDateSafe = (value) => {
    try {
      if (!value) return "-";
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    } catch (e) { return value; }
  };

  const formatTimeSafe = (value) => {
    try {
      if (!value) return "-";
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    } catch (e) { return value; }
  };

  useEffect(() => {
    if (tokenLoading) return;
    setLoading(true);
    fetchAllEvents().then(response => {
      const data = response.status === 200 ? (response.data || []) : [];
      setEvents(data);
      setLoading(false);

      // Auto-redirect to nearest upcoming event
      const now = new Date();
      const upcoming = data
        .filter(e => !e.cancelled && new Date(e.eventStartTime) > now)
        .sort((a, b) => new Date(a.eventStartTime) - new Date(b.eventStartTime));
      if (upcoming.length > 0) {
        navigate(`/booking/${upcoming[0].id}`);
      }
    }).catch(() => { setEvents([]); setLoading(false); });
  }, [tokenLoading, navigate]);

  const handleBookClick = (event) => {
    if (event.cancelled) return;
    navigate(`/booking/${event.id}`, { state: { event } });
  };

  if (tokenLoading) {
    return (
      <>
        <Navbar />
        <div className="bk-loading-wrap">
          <div className="bk-spinner"></div>
          <p>Signing you in...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />

      <div className="client-container">

        <div className="hero-section-event-list">
          <div className="hero-content">
            <h1 className="hero-title">Master Your Communication</h1>
          </div>
          <button className="my-bookings-btn-hero" onClick={handleMyBookingsClick}>
            My Bookings
          </button>
        </div>

        {loading && (
          <div className="bk-loading-wrap">
            <div className="bk-spinner"></div>
            <p>Loading events...</p>
          </div>
        )}

        {!loading && events.length === 0 && (
          <div className="center">
            <div className="empty-state">
              <div className="empty-icon">📅</div>
              <h3 className="empty-title">No events available</h3>
              <p className="empty-message">Check back soon for new communication workshops!</p>
            </div>
          </div>
        )}

        {!loading && events.length > 0 && (
          <div className="event-grid">
            {events.map(event => (
              <div key={event.id} className="enhanced-event-card">

                <div className="card-header">
                  <div className="category-badge">{event.category || 'Workshop'}</div>
                  <div className={`level-badge level-${(event.level || 'all-levels').toLowerCase().replace(' ', '-')}`}>
                    {event.level || 'All Levels'}
                  </div>
                </div>

                <div className="card-body">
                  {event.cancelled && <div className="cancelled-badge">Cancelled</div>}
                  <h3 className="enhanced-event-title">{event.title}</h3>
                  <p className="enhanced-event-description">{event.description}</p>

                  <div className="instructor-info">
                    <div className="instructor-avatar">👨🏫</div>
                    <div>
                      <div className="instructor-name">{event.speakerName || 'TBD'}</div>
                      {event.speakerBio && <div className="instructor-description">{event.speakerBio}</div>}
                    </div>
                  </div>

                  <div className="event-key-details">
                    <div className="key-detail-row">
                      <div className="key-detail-item">
                        <div className="detail-icon-large">📅</div>
                        <div className="detail-content">
                          <div className="detail-label-large">Date</div>
                          <div className="detail-value-large">{formatDateSafe(event.eventStartTime)}</div>
                        </div>
                      </div>
                      <div className="key-detail-item">
                        <div className="detail-icon-large">⏰</div>
                        <div className="detail-content">
                          <div className="detail-label-large">Time</div>
                          <div className="detail-value-large">{formatTimeSafe(event.eventStartTime)}</div>
                        </div>
                      </div>
                    </div>
                    <div className="key-detail-row">
                      <div className="key-detail-item duration">
                        <div className="detail-icon-large">⏱️</div>
                        <div className="detail-content">
                          <div className="detail-label-large">Duration</div>
                          <div className="detail-value-large">{event.eventDuration || 60} min</div>
                        </div>
                      </div>
                      <div className="key-detail-item zoom">
                        <div className="detail-icon-large zoom-icon">📹</div>
                        <div className="detail-content">
                          <div className="detail-label-large">Platform</div>
                          <div className="detail-value-large">{event.platform || 'Zoom'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-footer">
                  <div className="price-section">
                    <div className="pricing-container">
                      {event.price > 0 ? (
                        <>
                          {event.originalPrice > event.price && (
                            <span className="original-price">₹{event.originalPrice}</span>
                          )}
                          <span className="discounted-price">₹{event.price}</span>
                        </>
                      ) : (
                        <span className="discounted-price free-badge">FREE</span>
                      )}
                    </div>
                    {event.price > 0 && <span className="price-label">per person</span>}
                  </div>
                  <button
                    className={`enhanced-book-btn ${event.cancelled ? "disabled-btn" : ""}`}
                    onClick={() => handleBookClick(event)}
                    disabled={event.cancelled}
                  >
                    {event.cancelled ? "Event Cancelled" : "Book Now"}
                  </button>
                </div>

              </div>
            ))}
          </div>
        )}

      </div>

      {showLoginAlert && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000, padding: "16px" }}>
          <div style={{ background: "#fff", borderRadius: "16px", maxWidth: "400px", width: "100%", padding: "32px 24px", boxShadow: "0 10px 30px rgba(0,0,0,0.15)", textAlign: "center" }}>
            <h2 style={{ fontSize: "1.4rem", fontWeight: "700", marginBottom: "12px", color: "#222" }}>Login Required</h2>
            <p style={{ fontSize: "1rem", color: "#555", marginBottom: "24px" }}>You need to login to view your bookings.</p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button style={{ padding: "10px 20px", borderRadius: "8px", border: "1px solid #ccc", background: "#fff", color: "#333", cursor: "pointer", fontWeight: 500 }} onClick={() => setShowLoginAlert(false)}>Dismiss</button>
              <button style={{ padding: "10px 20px", borderRadius: "8px", border: "none", background: "#f97316", color: "#fff", cursor: "pointer", fontWeight: 600 }} onClick={() => navigate("/login")}>Go to Login</button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default EventListPage;
